"use client";

import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const filters = [{
  label: "All",
  value: "all"
}, {
  label: "Events",
  value: "events"
}, {
  label: "Updates",
  value: "updates"
}, {
  label: "Impact",
  value: "impact"
}, {
  label: "Members",
  value: "members"
}, {
  label: "Vision",
  value: "vision"
}];
const fallbackStories = [{
  id: "f1",
  niche: "Events",
  title: "Quarterly gathering brings local leaders together for change.",
  excerpt: "Our latest community summit focused on sustainable growth and local empowerment initiatives for the upcoming season.",
  stat: "120+ attendees · 5 speakers",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/community_summit.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/community_summit.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/community_summit.png-wvc-sizes",
  alt: "Community leaders meeting in a bright conference hall",
  link: "#"
}, {
  id: "f3",
  niche: "Impact",
  title: "Making the world a better place through collective action.",
  excerpt: "Our recent outreach programs have reached new milestones in providing essential resources to those in need.",
  stat: "500+ families · 10 regions",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/outreach_program.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/outreach_program.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/outreach_program.webp-wvc-sizes",
  alt: "Volunteers working together on a community project",
  link: "#"
}, {
  id: "f4",
  niche: "Members",
  title: "Celebrating our dedicated members and their contributions.",
  excerpt: "Meet the individuals who make our foundation possible through their unwavering support and commitment.",
  stat: "1k+ members · Growing",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/member_spotlight.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/member_spotlight.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/member_spotlight.png-wvc-sizes",
  alt: "Portrait of a smiling foundation member",
  link: "#"
}, {
  id: "f5",
  niche: "Vision",
  title: "Love and light: our guiding principles for the future.",
  excerpt: "Reflecting on our core values as we expand our reach and deepen our impact in the coming year.",
  stat: "Core values · Future focus",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/foundation_vision.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/foundation_vision.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/foundation_vision.png-wvc-sizes",
  alt: "Abstract representation of light and hope",
  link: "#"
}, {
  id: "f6",
  niche: "Updates",
  title: "Quarterly report highlights significant progress made.",
  excerpt: "A detailed look at our achievements over the last three months and our goals for the next quarter.",
  stat: "95% goal met · Success",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/quarterly_report.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/quarterly_report.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/quarterly_report.webp-wvc-sizes",
  alt: "Foundation report document on a wooden table",
  link: "#"
}];
function StoriesGrid({
  activeFilter
}: {
  activeFilter: string;
}) {
  const {
    posts,
    loading,
    hasNext,
    loadMore,
    isRefetching
  } = usePosts();
  const usePosts_hasData = !loading && posts && posts.length > 0;
  const stories = usePosts_hasData ? posts.map((post, i) => ({
    id: String(post.id),
    niche: post.categories && post.categories.length > 0 ? post.categories[0].name : "News",
    title: post.title,
    excerpt: post.excerpt ? post.excerpt.replace(/<[^>]*>/g, "").slice(0, 180) : "",
    stat: "",
    image: post.featuredImage || fallbackStories[i % fallbackStories.length].image,
    srcset: "",
    sizes: "",
    alt: post.title,
    link: post.link || "#",
    isDynamic: true
  })) : fallbackStories.map(s => ({
    ...s,
    isDynamic: false
  }));
  const filtered = activeFilter === "all" ? stories : stories.filter(s => s.niche.toLowerCase().includes(activeFilter));
  const visible = filtered.length > 0 ? filtered : stories;
  if (loading) {
    return <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="flex flex-col">
            <div className="aspect-[4/5] w-full animate-pulse rounded-3xl bg-muted" />
            <div className="mt-6 h-6 w-24 animate-pulse rounded-lg bg-muted" />
            <div className="mt-4 h-8 w-full animate-pulse rounded bg-muted" />
            <div className="mt-4 h-4 w-3/4 animate-pulse rounded bg-muted" />
          </div>)}
      </div>;
  }
  return <>
      <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {visible.map((story, i) => <article key={story.id} data-index={i} className="group flex flex-col">
            <Link to={story.link} className="block">
              <div className="relative overflow-hidden rounded-3xl" style={{
            aspectRatio: "4 / 5"
          }}>
                {story.isDynamic ? <img src={story.image} alt={story.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" style={{
              transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
            }} /> : <img src={story.image} data-wvc-srcset={story.srcset} data-wvc-sizes={story.sizes} alt={story.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" style={{
              transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
            }} />}
              </div>
            </Link>

            <div className="mt-6">
              <span className="inline-flex items-center rounded-lg border px-3.5 py-1.5 text-xs font-medium" style={{
            backgroundColor: "hsl(152 26% 14% / 0.08)",
            color: "hsl(152 26% 14%)",
            borderColor: "hsl(152 26% 14% / 0.28)"
          }}>
                {story.niche}
              </span>
            </div>

            <h3 className="mt-4 font-serif font-bold text-foreground transition-transform duration-300 group-hover:translate-x-1" style={{
          fontSize: "clamp(1.375rem, 2vw, 1.75rem)",
          lineHeight: "1.1",
          letterSpacing: "-0.02em",
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
        }}>
              <Link to={story.link} className="text-foreground hover:text-foreground">
                {story.title}
              </Link>
            </h3>

            {story.excerpt && <p className="mt-4 font-default text-base leading-relaxed" style={{
          color: "var(--muted-foreground)"
        }}>
                {story.excerpt}
              </p>}

            <div className="mt-6 flex items-end justify-between gap-4 border-t pt-4" style={{
          borderColor: "var(--border)"
        }}>
              <Link to={story.link} className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-foreground">
                <span>{"Read more"}</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              {story.stat && <span className="font-mono text-xs text-right" style={{
            color: "var(--muted-foreground)"
          }}>
                  {story.stat}
                </span>}
            </div>
          </article>)}
      </div>

      {hasNext && <div className="mt-16 flex justify-center">
          <Button variant="ghost" onClick={async () => {
        await loadMore();
      }} disabled={isRefetching} className="group inline-flex items-center gap-2 bg-transparent px-6 py-3 text-base font-medium text-foreground hover:bg-transparent hover:text-foreground">
            <span className="underline underline-offset-4">
              {isRefetching ? "Loading..." : "Load more"}
            </span>
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Button>
        </div>}
    </>;
}
export default function FoundationNews() {
  const [activeFilter, setActiveFilter] = useState("all");
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return;
}